import { useTaskCalendarSyncControls } from '@griply/hooks';
import { useCalendarTaskSync } from 'features/external-connections';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarSyncMenu,
  getCalendarSyncIcon,
} from 'shared/components/ui/calendar-sync-menu';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Task } from 'shared/types/task';

export type TaskCalendarSyncButtonProps = {
  task: Task;
};

// Unlike the calendar event card's badge, this one is always visible while the
// task has a calendar it could sync to — there is no hover surface to reveal it.
// It also always opens the menu rather than syncing on the first click: a header
// control is deliberate, where the badge is a quick affordance on a dense card.
export const TaskCalendarSyncButton: React.FC<TaskCalendarSyncButtonProps> = ({
  task,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const tasks = useMemo(() => [task], [task]);
  const calendarSync = useCalendarTaskSync(tasks);
  const {
    syncState,
    toggleSync: handleToggleSync,
    toggleVisibility: handleToggleVisibility,
  } = useTaskCalendarSyncControls(task, calendarSync);

  if (!syncState) {
    return null;
  }

  return (
    <>
      <IconButton
        ref={buttonRef}
        icon={getCalendarSyncIcon(syncState.isSynced, syncState.isHidden)}
        color={IconButtonColors.Default}
        strokeWidth={1.5}
        size={IconButtonSizes.Medium}
        highlighted={syncState.isSynced}
        onClick={openMenu}
        aria-label={t('calendar.event-sync.aria-label')}
        preventDrag
      />

      {!syncState.isSynced && (
        <Tooltip
          referenceElement={buttonRef}
          label={t('calendar.event-sync.start')}
        />
      )}

      {menuOpen && (
        <CalendarSyncMenu
          isSynced={syncState.isSynced}
          isHidden={syncState.isHidden}
          onToggleSync={handleToggleSync}
          onToggleVisibility={handleToggleVisibility}
          onClose={closeMenu}
          referenceElement={buttonRef}
        />
      )}
    </>
  );
};
